/**
 * @todo rename to scopes
 * @todo use memo for computation
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { chunk } from 'lodash'
import SVG from 'react-inlinesvg'
import { apiJSON } from '@shared/utils/api/api.util'
import { useRouter } from '@shared/hooks/use-router'
import { ProgressButton } from '@shared/components/progress-button.component'
import { useScopesQuery } from '@shared/hooks/use-scopes-query.hook'
import { originalIcons } from '@shared/components/icons/icons'
import { Scope } from '@shared/types'
import { usePersistedTabChange } from '@shared/hooks/use-persisted-tab-change'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import {
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'

const { Check } = originalIcons

export function ManageScopesPage() {
  const queryClient = useQueryClient()
  const { query, navigate } = useRouter<{
    companyID: string
    auditID?: string
  }>()
  const { companyID, auditID } = query
  const isEditMode = Boolean(auditID)
  const { onTabChange } = usePersistedTabChange(
    `currentTab.${companyID}.${auditID}`
  )

  const { data: continueAuditData, isFetching: areContinueAuditDataLoading } =
    useQuery(
      `companies/${companyID}/Audits/${auditID}`,
      () => apiJSON(`companies/${companyID}/Audits/${auditID}`),
      { enabled: Boolean(auditID) }
    )

  const fetchedAuditIDs = useMemo(() => {
    return (
      continueAuditData?.scopedRequirements?.map(
        (scope: { id: number }) => scope.id
      ) ?? []
    )
  }, [continueAuditData])

  const classes = useStyles()
  const [scopeIDs, setScopesIDs] = useState<(number | string)[]>([])

  const { areScopesLoading, scopes } = useScopesQuery()
  const scopeParts = chunk<Scope>(scopes ?? [], 4)

  const onScopesSelect = useCallback(
    (e: any, selectedScopes: (number | string)[]) => {
      setScopesIDs(selectedScopes)
    },
    []
  )

  const { mutateAsync: createAudit, isLoading: isAuditCreating } = useMutation<{
    id: number
  }>(() =>
    apiJSON(
      `companies/${companyID}/Audits`,
      {
        companyID,
        scopeIDs,
      },
      { method: 'POST' }
    )
  )

  const { mutateAsync: editScopes, isLoading: isScopeEditing } =
    useMutation<{}>(
      () =>
        apiJSON(
          `companies/${companyID}/Audits/${auditID}/scopes`,
          { scopeIds: scopeIDs },
          { method: 'PUT' }
        ),
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries(`audits/${auditID}`)
          onTabChange(null, '0')
        },
      }
    )

  const onCancel = useCallback(() => {
    isEditMode
      ? navigate(`/company-profile/${companyID}/${auditID}`)
      : navigate(-1)
  }, [navigate, isEditMode, companyID, auditID])

  const onScopesSubmit = useCallback(async () => {
    if (isEditMode) {
      await editScopes()

      navigate(`/company-profile/${companyID}/${auditID}`)
    } else {
      const audit = await createAudit()

      if (audit) {
        navigate(`/company-profile/${companyID}/${audit.id}`)
      }
    }
  }, [auditID, editScopes, isEditMode, companyID, createAudit])

  useEffect(() => {
    if (
      !areContinueAuditDataLoading &&
      fetchedAuditIDs.length > 0 &&
      scopeIDs.length === 0
    ) {
      setScopesIDs(fetchedAuditIDs)
    }
  }, [areContinueAuditDataLoading])

  return !areScopesLoading &&
    !isScopeEditing &&
    !areContinueAuditDataLoading ? (
    <>
      <h2 className={classes.header}>
        Wybierz obszary które będą weryfikowane podczas audytu
      </h2>
      {scopeParts.map((scopesChunk) => (
        <ToggleButtonGroup
          key={scopesChunk.map(({ scopeID }) => scopeID).join('-')}
          size="large"
          orientation="horizontal"
          value={scopeIDs}
          onChange={onScopesSelect}
          className={classes.scopesContainer}
        >
          {scopesChunk!.map((scope) => {
            return (
              <ToggleButton
                key={scope.scopeID}
                className={classes.scopeTile}
                value={scope.scopeID}
              >
                <p className={classes.scopeLabel}>{scope.name}</p>
                {scope.imageURL ? (
                  <SVG src={scope.imageURL} className={classes.scopeIcon} />
                ) : null}
                {scopeIDs?.includes(scope.scopeID) ? (
                  <Check className={classes.scopeSelectedIcon} />
                ) : null}
              </ToggleButton>
            )
          })}
        </ToggleButtonGroup>
      ))}
      <ButtonGroup className={classes.actionButtonsContainer}>
        <Button variant="outlined" onClick={onCancel}>
          Anuluj
        </Button>
        <ProgressButton
          isLoading={isAuditCreating}
          onClick={onScopesSubmit}
          disabled={isAuditCreating || scopeIDs?.length === 0}
          variant="contained"
          color="primary"
          className={classes.actionButtonSubmit}
        >
          Dalej
        </ProgressButton>
      </ButtonGroup>
    </>
  ) : (
    <BackdropProgress />
  )
}

export const useStyles = makeStyles((theme: any) => ({
  scopesContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  scopeTile: {
    display: 'flex',
    flexDirection: 'column',
    width: '200px',
    height: '200px',
    color: theme.palette.grey[700],
    backgroundColor: theme.palette.grey[100],
    margin: '5px 10px',
    border: `1px solid ${theme.palette.grey[300]}`,
    /**
     * @todo find better way, probably overriding .MuiToggleButtonGroup-groupedHorizontal:not(:last-child)
     */
    borderLeft: `1px solid ${theme.palette.grey[300]} !important`,
    '& span:first-child': {
      flexDirection: 'column',
    },
  },
  scopeIcon: {
    display: 'block',
    width: 80,
    height: 80,
    fill: theme.palette.primary.main,
  },
  scopeLabel: {
    marginTop: 'auto',
  },
  scopeSelectedIcon: {
    position: 'absolute',
    bottom: 10,
    right: 10,
  },
  header: {
    color: theme.palette.grey[600],
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightLight,
  },
  actionButtonsContainer: {
    marginTop: '30px',
    marginLeft: 'auto',
    marginRight: '10px',
  },
  actionButtonSubmit: {
    marginLeft: 20,
  },
}))
